import { titleTierLabel, titleTierFor } from '../../../lib/titleTierView.js'
import SchoolTeamCoverage from '../../bowl/SchoolTeamCoverage.jsx'
import {
  daysToEvent, formatDate, formatCountdownFor, pursuitMeta, sportMeta, ownerName,
} from '../../../lib/championships.js'
import SubmitLink from './SubmitLink.jsx'
import HeroBadge, { PursuitChip } from './HeroBadge.jsx'
import NextStep from './NextStep.jsx'
import ChampionCell from './ChampionCell.jsx'
import { COMMUNITY_HERO, GREEK_PURSUIT } from '../recognition.js'
import { CHEER_PARTNERSHIP, partnershipMeta } from '../cheer.js'
import DateStamp from './DateStamp.jsx'
import TradeChip from '../../trades/TradeChip.jsx'
import PursuitMove from './PursuitMove.jsx'
import CardActions from './CardActions.jsx'
import styles from './pursuit.module.css'

/** The canonical championship card — honest about every gap. Color-coded by
 * sport (left accent) and pursuit status (dot). Clickable when `onOpen` is
 * provided (opens the detail drawer + action console).
 *
 * `dated: false` is an EMPLOYEE-RECOGNITION space (Corporate): those records
 * have no event to count down to, so the date row is absent entirely instead of
 * reading "NO DATE · no date" on every single card. The card shows what
 * actually qualifies the account instead — industry, headcount and HQ. */
export default function ChampCard({ c, now, onOpen, dated = true, rowAction = null, onAccepted,
  /* THE BOARD'S CONTROLS (2026-10-26, [Jarvis · Claude]). Only the Pursuit
   * board passes `board`; every other surface that renders this card is
   * unchanged. `onMove` absent = a read-only status line, said in words. */
  board = false, onMove = null, actions = null }) {
  const days = daysToEvent(c, now)
  const pm = pursuitMeta(c.pursuit)
  const sm = sportMeta(c.sport)
  const urgent = days != null && days >= 0 && days <= 14
  const overdueish = days != null && days < 0
  const showDate = dated || !!c.effDate
  // THE RECOGNITION / PURSUIT KINDS. Keyed off the record so a card only grows
  // the hero badge in a police / fire space and the pursuit line in Greek — a
  // championship or corporate card is byte-identical to before.
  const heroKind = c.kind === COMMUNITY_HERO
  const greekKind = c.kind === GREEK_PURSUIT
  const cheerKind = c.kind === CHEER_PARTNERSHIP
  const partnership = cheerKind ? partnershipMeta(c) : null

  return (
    <article
      className={`${styles.card} ${onOpen ? styles.cardClickable : ''}`}
      style={{ borderLeft: `4px solid ${sm.color}` }}
      onClick={onOpen ? () => onOpen(c) : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      data-record-id={c.id}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(c) } : undefined}
    >
      <div className={styles.cardTop}>
        <div className={styles.cardName}>{c.name}</div>
        <span className={styles.pursuitDot} style={{ background: pm.color }} title={`${pm.label} · ${sm.label}`} />
      </div>

      <div className={styles.badges}>
        {c.titleTier && <span className={styles.badge} title={titleTierFor(c).titleTierReason}>{titleTierLabel(c)}</span>}
        {c.sport && <span className={styles.badge}>{c.sport}</span>}
        {/* WHAT IT'S FOR — the RFP's product category, first-class on the card
            because it is how a rep decides whether the bid is even ours. */}
        {c.product && <span className={styles.badge} title={c.productLabel || 'Product category'}>{c.product}</span>}
        {/* WHAT THEY DO — the corporate qualifier, in the same slot for the
            same reason: it is how a rep decides whether the account is ours. */}
        {c.industry && <span className={styles.badge} title={c.industryLabel || 'Industry'}>{c.industry}</span>}
        {c.level && <span className={styles.badgeDim} title={c.levelLabel || 'Level'}>{c.level}</span>}
        {/* A pursuit row shows its own status word ("Open"), not just the
            canonical bucket the dot encodes. */}
        {c.sourceType && c.stageLabel && <span className={styles.badgeDim}>{c.stageLabel}</span>}
        {c.state && c.state !== 'NATIONAL'
          ? <span className={styles.badgeDim}>{c.state}</span>
          : <span className={styles.badgeGap}>no state</span>}
      </div>

      {/* THE STATUS, AS A CONTROL. The coloured dot above only ever had a
          tooltip; on a phone there is no hover, so the status was invisible
          and unmovable. */}
      {board && <PursuitMove c={c} onMove={onMove} />}

      {showDate && (
        <div className={styles.dateRow}>
          <span className={c.effDate ? styles.date : styles.dateGap}>
            {c.effDate ? formatDate(c.effDate) : 'NO DATE'}
            {c.dateEstimated && <span className={styles.estBadge} title="Estimated from season year + sport window">est</span>}
            {/* A PROJECTION SAYS SO (schedule-is-projected-not-real). The stamp
                below draws nothing for one, so without this the guess read as
                an event date. */}
            {c.effDate && !c.dateEstimated && c.dateConfidence && c.dateConfidence !== 'confirmed' && (
              <span className={styles.estBadge} title="Projected, not a published event date">projected</span>
            )}
          </span>
          {/* THE STAMP — see lib/championships.js#dateStamp. Draws nothing for
              a projected date, which is most of them. */}
          <DateStamp c={c} compact />
          <span className={`${styles.countdown} ${urgent ? styles.urgent : ''} ${overdueish ? styles.past : ''}`}>
            {formatCountdownFor(c, days)}
          </span>
        </div>
      )}

      {/* THE SIZE OF THE PROGRAM. Headcount is the whole basis of a service-
          award program, so it takes the slot the event date holds elsewhere —
          with HQ beside it. Both say so honestly when HubSpot hasn't got them
          yet rather than rendering a zero or an empty line. */}
      {(c.employees != null || c.hqLocation) && (
        <div className={styles.dateRow}>
          <span className={c.employees != null ? styles.date : styles.dateGap} title={c.employeesLabel || 'Total employees'}>
            {c.employees != null ? `${c.employees.toLocaleString()} employees` : 'headcount unknown'}
          </span>
          {c.hqLocation && (
            <span className={styles.dateSub} title={c.hqLabel || 'HQ location'}>{c.hqLocation}</span>
          )}
        </div>
      )}

      {/* THE COMMUNITY HERO — the headline fact about a police or fire
          department: does it have a nominated hero, who, and how many years of
          service. A department with none says NO HERO, which is the gap this
          space exists to close. */}
      {heroKind && (
        <div className={styles.heroRow}>
          <HeroBadge record={c} />
        </div>
      )}

      {/* THE GREEK PURSUIT — what is being pursued here, when it opened and
          when it was won. All four come from the associated pursuit deal; a
          missing one is rendered as missing, never as today's date. */}
      {greekKind && (
        <>
          <div className={styles.heroRow}>
            <PursuitChip record={c} />
            {c.orgType && <span className={styles.badgeDim}>{c.orgType}</span>}
          </div>
          {(c.pursuitDate || c.wonDate) && (
            <div className={styles.dateRow}>
              <span className={styles.dateSub}>
                {c.pursuitDate ? `opened ${formatDate(c.pursuitDate)}` : 'open date not recorded'}
              </span>
              <span className={c.wonDate ? styles.date : styles.dateSub}>
                {c.wonDate ? `won ${formatDate(c.wonDate)}` : 'not won'}
              </span>
            </div>
          )}
        </>
      )}

      {/* THE CHEER GYM. Two facts decide everything about a gym: where it is on
          the partnership pipeline (and a partnership is BOTH halves, so the
          contract and the exclusive jewelry program are stated separately), and
          how big its championship footprint is — the titles its teams have won,
          which is exactly what makes an unpartnered gym worth chasing. A zero
          is a real, useful answer here, not a gap. */}
      {cheerKind && (
        <>
          <div className={styles.heroRow}>
            <span
              className={styles.statusChip}
              style={{ color: partnership.color, borderColor: partnership.color + '55', background: partnership.color + '12' }}
              title={partnership.hint}
            >
              {partnership.partner ? '✓ ' : ''}{partnership.label}
            </span>
            <span className={partnership.signed ? styles.badgeDim : styles.badgeGap}>
              {partnership.signed ? 'contract signed' : 'no contract'}
            </span>
            <span className={partnership.jewelry.live ? styles.badgeDim : styles.badgeGap}>
              {partnership.jewelry.live ? 'jewelry live' : 'no jewelry program'}
            </span>
          </div>
          <div className={styles.dateRow}>
            <span className={c.titleCount ? styles.champ : styles.dateSub}>
              {c.titleCount
                ? `♛ ${c.titleCount} championship${c.titleCount === 1 ? '' : 's'} · ${c.teamCount} team${c.teamCount === 1 ? '' : 's'}`
                : 'no championship titles matched'}
            </span>
            {c.lastTitleAt && <span className={styles.dateSub}>last {formatDate(c.lastTitleAt)}</span>}
          </div>
        </>
      )}

      {/* BOTH DATES. The open date is a proxy on the RFP line (the day the
          engine SURFACED the posting, not the day the issuer opened it), so it
          is labelled for what it actually is. */}
      {c.openDate && (
        <div className={styles.dateRow}>
          <span className={styles.dateSub} title={c.openDateIsProxy ? 'When the RFP engine surfaced this posting — the issuer’s true open date is not captured yet' : undefined}>
            {c.openDateLabel || 'Opened'} {formatDate(c.openDate)}
            {c.openDateIsProxy && <span className={styles.estBadge} title="Proxy — scan date, not the issuer&rsquo;s open date">proxy</span>}
          </span>
        </div>
      )}

      <div className={styles.metaRow}>
        {ownerName(c)
          ? <span className={styles.owner}>{ownerName(c)}</span>
          : <span className={styles.gapFlag}>UNASSIGNED</span>}
        <SchoolTeamCoverage record={c}/>
        {c.sourceType
          ? (c.issuer ? <span className={styles.incumbent}>{c.issuer}</span> : null)
          : (
            <span className={styles.incumbent}>
              inc: {c.incumbent || <em className={styles.unknown}>unknown</em>}
            </span>
          )}
      </div>

      {/* THE WINNER, PROMOTED.
        *
        * This used to be `♛ {c.champion}` sitting below the owner and the
        * incumbent — the single most important fact on the record rendered as
        * the least prominent thing on it, with its trust hidden behind a
        * floating button and its dossier two clicks away.
        *
        * Now it carries its own trust verdict, its own problems and its own
        * way in, computed from the record with no extra request. See
        * ChampionCell for why that is possible now and was not before. */}
      <ChampionCell record={c} onDossier={onOpen} onAccepted={onAccepted} />
      {/* The trade on this record, inline (2026-10-19). Nothing when there is none. */}
      <TradeChip recordId={c.id} />

      {/* The official participate/submit channel. Renders only when the record
          carries one, so a championship card is untouched. */}
      {c.sourceUrl ? <div className={styles.cardLink}><SubmitLink record={c} /></div> : null}

      {/* THE GUIDED NEXT STEP — what to DO about this record, not just what is
          true about it. One step, numbered, with the one line of
          what-happens-next and the button that does it. It replaces the bare
          action button: the button is still there, inside the step, but now the
          rep is told which step they are on and why. Absent entirely in every
          space that has no pathway (`nextStepFor` returns null on them). */}
      {rowAction && <NextStep record={c} action={rowAction} onOpen={onOpen} />}

      {board && <CardActions c={c} onOpen={onOpen} actions={actions} />}
    </article>
  )
}
